import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CHAPTER_LABELS } from "./ReadCompliance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MODES = [
  { v: "ask", label: "Ask the manual" },
  { v: "draft", label: "Draft text" },
  { v: "summarize", label: "Summarize" },
];
const SUGGESTIONS = [
  "What's the deadline after we receive an EOB?",
  "Summarize Chapter 11 for a new assist",
  "Draft a change request asking to simplify the monthly training log",
];

export default function SopAssistant({ active }) {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("ask");
  const [contextChapter, setContextChapter] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);
  const skipLoadRef = useRef(false);

  const loadSessions = useCallback(async () => {
    const r = await axios.get(`${API}/assistant/sessions`);
    setSessions(r.data);
    return r.data;
  }, []);

  useEffect(() => { if (active === "ai") loadSessions().catch(() => {}); }, [active, loadSessions]);
  useEffect(() => {
    if (!sid) { setMessages([]); return; }
    if (skipLoadRef.current) { skipLoadRef.current = false; return; }
    axios.get(`${API}/assistant/sessions/${sid}/messages`).then((r) => setMessages(r.data)).catch(() => setMessages([]));
  }, [sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  const send = async (textArg) => {
    const text = (textArg || input).trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    let sessionId = sid;
    try {
      if (!sessionId) {
        const r = await axios.post(`${API}/assistant/sessions`, { title: "New conversation" });
        sessionId = r.data.id;
        skipLoadRef.current = true;
        setSid(sessionId);
      }
      setMessages((p) => [...p, { role: "user", content: text }, { role: "assistant", content: "" }]);
      let contextText = "";
      if (contextChapter) {
        contextText = (document.getElementById(contextChapter)?.textContent || "").slice(0, 60000);
        if (!contextText) toast.error("Could not read that chapter's text — answering from the brief instead");
      }
      const resp = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("ech_token")}` },
        body: JSON.stringify({
          session_id: sessionId, message: text, mode,
          context_chapter: contextChapter ? (CHAPTER_LABELS[contextChapter] || contextChapter) : "",
          context_text: contextText,
        }),
      });
      if (!resp.ok) throw new Error((await resp.json()).detail || "Request failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.delta) {
            setMessages((p) => {
              const n = [...p];
              n[n.length - 1] = { ...n[n.length - 1], content: n[n.length - 1].content + ev.delta };
              return n;
            });
          } else if (ev.error) {
            toast.error(ev.error);
          }
        }
      }
      loadSessions().catch(() => {});
    } catch (e) {
      toast.error(e.message || "The assistant is unavailable right now");
      setMessages((p) => p.filter((m) => m.content !== ""));
    }
    setStreaming(false);
  };

  return (
    <section className={`chapter utility${active === "ai" ? " active" : ""}`} id="ai">
      <div className="eyebrow">Working record · Claude Sonnet 4.6 grounded in manual v1.8-D — cites SOPs, never guesses, not legal counsel</div>
      <h1>SOP Assistant</h1>
      <p className="lede">Ask the manual a question, have it draft SOP language or emails, or get a plain-English summary. Answers cite chapters and SOPs and distinguish calendar vs business days. Anything the manual doesn't cover gets routed to the owning lane or counsel — the assistant does not improvise policy.</p>

      <div className="formcard" data-testid="ai-disclosure">
        <div className="formcard-title">Model &amp; data disclosure</div>
        <p style={{ margin: 0, color: "var(--mute)", fontSize: 13 }}>
          Model: <b>Anthropic Claude Sonnet 4.6</b>, called <b>server-side</b> through Emergent's managed LLM gateway — the browser never talks to Anthropic directly. What leaves ECH's server per message: your question, the last few turns of the conversation, a condensed brief of this manual, and — only when you select a chapter as context — that chapter's text. Conversation history is stored in <b>ECH's own database</b> for continuity. Anthropic processes API traffic to generate responses and, per its commercial API terms, does not use API inputs or outputs to train its models. No page content is sent unless you explicitly select it as context. Do not paste PHI or patient identifiers into the assistant (SOP 6.2 — minimum necessary).
        </p>
      </div>

      <div className="formcard" data-testid="ai-panel">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <select className="inp" style={{ maxWidth: 320 }} value={sid} onChange={(e) => setSid(e.target.value)} data-testid="ai-session-select">
            <option value="">＋ New conversation</option>
            {sessions.map((s) => (<option key={s.id} value={s.id}>{s.title}</option>))}
          </select>
          {MODES.map((m) => (
            <button key={m.v} className={`btn ${mode === m.v ? "" : "ghost"}`} onClick={() => setMode(m.v)} data-testid={`ai-mode-${m.v}`}>{m.label}</button>
          ))}
          <select className="inp" style={{ maxWidth: 300 }} value={contextChapter} onChange={(e) => setContextChapter(e.target.value)} title="Ground answers in one chapter's full text" data-testid="ai-context-select">
            <option value="">Ground in: whole manual (brief)</option>
            {Object.entries(CHAPTER_LABELS).map(([id, label]) => (<option key={id} value={id}>Ground in: {label} (full text)</option>))}
          </select>
        </div>

        <div style={{ maxHeight: 480, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, padding: "4px 2px", marginBottom: 12 }} data-testid="ai-messages">
          {messages.length === 0 && (
            <div>
              <p className="cap-sub" style={{ marginBottom: 8 }}>Try one of these:</p>
              {SUGGESTIONS.map((s) => (
                <button key={s} className="vr-link" style={{ display: "block", marginBottom: 6, textAlign: "left" }} onClick={() => send(s)} data-testid="ai-suggestion">{s}</button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{
              alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "85%",
              background: m.role === "user" ? "var(--navy)" : "var(--card)",
              color: m.role === "user" ? "#fff" : "var(--ink)",
              border: m.role === "user" ? "none" : "1px solid var(--line)",
              borderRadius: 8, padding: "10px 14px", fontSize: 14, whiteSpace: "pre-wrap",
            }} data-testid={`ai-msg-${m.role}`}>
              {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <input className="inp" style={{ flex: 1 }} placeholder={mode === "ask" ? "Ask the manual…" : mode === "draft" ? "Describe what to draft…" : "What should I summarize?"}
            value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }} disabled={streaming} data-testid="ai-input" />
          <button className="btn" onClick={() => send()} disabled={streaming || !input.trim()} data-testid="ai-send">{streaming ? "Thinking…" : "Send"}</button>
        </div>
      </div>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Conversations are saved per user. The assistant is grounded in a curated brief of the current manual — when the manual changes, the brief updates with it. It is informational support, not legal advice and not a substitute for the SOP text itself.</p>
    </section>
  );
}
